/**
 * Country / region selection, the step V2 puts between "New data request" and the wizard.
 *
 * V2 lists every country and routes USA to the domestic wizard and everything else to the
 * international one. Slice 1 ports only the domestic (US) flow, so this offers the United
 * States and marks the rest as not yet available rather than pretending to route them.
 */
import { Button, makeStyles, shorthands, tokens } from '@fluentui/react-components';
import { Globe24Regular as Globe } from '@fluentui/react-icons';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/fluent-adapter';

const useStyles = makeStyles({
  page: {
    maxWidth: '880px',
    marginLeft: 'auto',
    marginRight: 'auto',
    ...shorthands.padding(tokens.spacingVerticalXXL, tokens.spacingHorizontalL),
    display: 'flex',
    flexDirection: 'column',
    rowGap: tokens.spacingVerticalL,
    alignItems: 'center',
  },
  header: {
    textAlign: 'center',
    display: 'flex',
    flexDirection: 'column',
    rowGap: tokens.spacingVerticalS,
    alignItems: 'center',
  },
  iconWrap: {
    width: '64px',
    height: '64px',
    borderRadius: '9999px',
    backgroundColor: tokens.colorBrandBackground2,
    color: tokens.colorBrandForeground1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  title: { fontSize: tokens.fontSizeHero700, fontWeight: tokens.fontWeightSemibold, margin: 0 },
  sub: { color: tokens.colorNeutralForeground3, maxWidth: '640px', margin: 0 },
  card: { width: '100%' },
  grid: {
    display: 'grid',
    gridTemplateColumns: '1fr',
    gap: tokens.spacingHorizontalM,
    '@media (min-width: 640px)': { gridTemplateColumns: '1fr 1fr' },
  },
  countryBtn: {
    justifyContent: 'flex-start',
    height: 'auto',
    textAlign: 'left',
    ...shorthands.padding(tokens.spacingVerticalL, tokens.spacingHorizontalL),
  },
  countryInner: { display: 'flex', flexDirection: 'column', alignItems: 'flex-start' },
  countryName: { fontWeight: tokens.fontWeightSemibold },
  countryCode: { color: tokens.colorNeutralForeground3, fontSize: tokens.fontSizeBase200 },
  soon: {
    marginTop: tokens.spacingVerticalM,
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
    textAlign: 'center',
  },
  actions: { display: 'flex', justifyContent: 'flex-start', width: '100%' },
});

interface CountryStepProps {
  onSelect: (country: string) => void;
  onSelectEEvidence: () => void;
  onCancel: () => void;
}

export function CountryStep({ onSelect, onSelectEEvidence, onCancel }: CountryStepProps) {
  const styles = useStyles();

  return (
    <div className={styles.page}>
      <div className={styles.actions}>
        <Button appearance="subtle" onClick={onCancel}>
          Cancel
        </Button>
      </div>

      <div className={styles.header}>
        <div className={styles.iconWrap}>
          <Globe />
        </div>
        <h1 className={styles.title}>Select country / region</h1>
        <p className={styles.sub}>
          Choose the country for which you are submitting this law-enforcement request.
        </p>
      </div>

      <Card className={styles.card}>
        <CardContent>
          <div className={styles.grid}>
            <Button
              appearance="outline"
              className={styles.countryBtn}
              onClick={() => onSelect('United States of America')}
            >
              <span className={styles.countryInner}>
                <span className={styles.countryName}>United States of America</span>
                <span className={styles.countryCode}>USA</span>
              </span>
            </Button>
          </div>
          <p className={styles.soon}>
            Other international request types are not yet available in this release.
          </p>
        </CardContent>
      </Card>

      <Card className={styles.card}>
        <CardHeader>
          <CardTitle>European E-Evidence</CardTitle>
          <CardDescription>
            Submit a completed Form 1 production order or Form 2 preservation order.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button appearance="primary" onClick={onSelectEEvidence}>
            Start E-Evidence request
          </Button>
          <p className={styles.soon}>
            Development preview only. End-to-end CMS submission is not yet available.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
